/**
 * Ngày lịch của hệ thống: chuỗi `YYYY-MM-DD`, **không mang múi giờ**.
 *
 * ## ⚠️ Mọi phép tính ở đây theo UTC, và đó không phải tuỳ chọn
 *
 * `CalendarDate` trong `@apm/contracts` là một ngày trên lịch — "hạn chót
 * 15/09" là ngày 15 tháng 9 với mọi người, bất kể họ ngồi ở đâu. Dùng
 * `new Date('2026-09-15')` rồi đọc `getDate()` ở UTC+7 vẫn ra 15, nhưng
 * `new Date(2026, 8, 15).toISOString()` ở UTC-5 lại ra `2026-09-15T05:00:00Z`
 * — và cắt 10 ký tự đầu thì vẫn đúng. Chỗ chết người là chiều ngược lại và các
 * phép cộng trừ: một người ở UTC+7 mở lịch lúc 7 giờ sáng, `new Date()` cho ra
 * *hôm nay* theo giờ địa phương nhưng `toISOString()` vẫn còn là *hôm qua*.
 *
 * Đây đúng là cạm bẫy mà `features/gantt/scale.ts` đã ghi lại và tránh. File
 * này giữ nguyên quy ước ấy, đặt ở `lib/` để component dùng chung
 * (`<DatePicker>`) không phải import ngược từ một feature.
 */

import { calendarDateIn } from '@apm/contracts';
import { orgTimezone } from './org-timezone';

/** `YYYY-MM-DD` → `Date` ở nửa đêm UTC. */
export function parseDate(value: string): Date {
  return new Date(`${value}T00:00:00Z`);
}

/** `Date` → `YYYY-MM-DD`. */
export function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Mốc nửa đêm UTC của một `Date`, tính bằng milli giây. */
export function startOfDay(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function addDays(date: Date, days: number): Date {
  return new Date(startOfDay(date) + days * 86_400_000);
}

export function dayDiff(from: Date, to: Date): number {
  return Math.round((startOfDay(to) - startOfDay(from)) / 86_400_000);
}

/**
 * Hôm nay **của tổ chức**, dạng `YYYY-MM-DD` — APM-TZ-i.
 *
 * Trước APM-TZ nó đọc giờ **của trình duyệt**: đúng với người ngồi ở Việt Nam,
 * nhưng một người mở app trên máy đặt giờ khác thấy "hôm nay" khác cả đội. Và
 * `new Date().toISOString().slice(0, 10)` còn tệ hơn: 7 giờ sáng ở UTC+7 vẫn là
 * *hôm qua* theo UTC.
 */
export function today(now = new Date()): string {
  return calendarDateIn(now, orgTimezone());
}

/**
 * Hôm nay của tổ chức dưới dạng **nửa đêm UTC** — APM-TZ-l.
 *
 * Cho các hàm thuần ở `@apm/contracts` (kỳ OKR, `goalPeriod`, `evaluateGoal`)
 * đọc ngày bằng `getUTC*`: chúng coi `at` là một ngày lịch. Không phải một mốc
 * thời gian — đừng dùng nó để đo "bao lâu nữa".
 */
export function todayAnchor(now = new Date()): Date {
  return parseDate(today(now));
}

/** Hôm nay của tổ chức cộng `days` ngày (âm = lùi), dạng `YYYY-MM-DD`. */
export function todayPlus(days: number, now = new Date()): string {
  return formatDate(addDays(parseDate(today(now)), days));
}

/** Cộng tháng, giữ nguyên ngày trong tháng khi có thể. */
export function addMonths(date: Date, months: number): Date {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + months;
  const day = date.getUTCDate();

  // 31/01 + 1 tháng: tháng 2 không có ngày 31. Kẹp về ngày cuối tháng thay vì
  // để `Date.UTC` tràn sang tháng 3 — người bấm "tháng sau" không mong nhảy hai
  // tháng.
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day, lastDay)));
}

export function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

export function isSameDay(a: Date, b: Date): boolean {
  return startOfDay(a) === startOfDay(b);
}

/**
 * Lưới sáu tuần cho một tháng, **luôn 42 ô**.
 *
 * Cố định số hàng chứ không co giãn theo tháng: một lịch cao 5 hàng ở tháng
 * này và 6 hàng ở tháng kia sẽ nhảy giật mỗi lần bấm mũi tên, và nút "tháng
 * sau" chạy mất khỏi ngón tay người dùng.
 *
 * Tuần bắt đầu **thứ Hai** — quy ước Việt Nam, và cũng là quy ước mà trục thời
 * gian của Gantt đang dùng.
 */
export function monthGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  // `getUTCDay()`: 0 là Chủ nhật. Đổi sang 0 = thứ Hai.
  const offset = (first.getUTCDay() + 6) % 7;
  const start = addDays(first, -offset);

  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

/** Kẹp một ngày vào khoảng cho phép. Trả về `null` khi không có ràng buộc nào. */
export function outOfRange(date: string, min?: string, max?: string): boolean {
  if (min !== undefined && date < min) return true;
  if (max !== undefined && date > max) return true;
  return false;
}

// ── Hiển thị cho người đọc ──────────────────────────────────────────────────

/**
 * Thứ có thể là một ngày.
 *
 * `number` là **epoch milli giây** — thanh trượt thời gian của Replay giữ mốc
 * dưới dạng số, và bắt nó bọc `new Date(...)` ở mọi chỗ gọi là đúng thứ vòng
 * sweep này dọn đi.
 */
export type DateLike = Date | string | number | null | undefined;

/**
 * Ngày cho người đọc: **`dd/MM/yyyy`** — quyết định của người dùng, 2026-09-21.
 *
 * ## ⚠️ Vì sao không dùng `toLocaleDateString()`
 *
 * Nó đọc locale của **trình duyệt người xem**, nên hai người mở cùng một báo
 * cáo thấy hai thứ khác nhau: `9/15/2026` ở máy `en-US`, `15/9/2026` ở máy
 * `vi-VN`. Trên một màn hình đánh giá con người hay một bảng hạn chót, "03/04"
 * đọc thành mùng 3 tháng 4 hay mùng 4 tháng 3 là một khác biệt có hậu quả.
 *
 * Trước hàm này, repo có **ba** kiểu cùng lúc: `toLocaleDateString()`, chuỗi
 * ISO `YYYY-MM-DD` in thẳng, và vài chỗ tự ghép. Mọi chỗ hiển thị ngày đi qua
 * đây; `<input type="date">` thì **không** — nó đòi `YYYY-MM-DD` theo chuẩn
 * HTML, và đó là giá trị máy đọc chứ không phải phần hiển thị.
 *
 * ⚠️ Dấu gạch chéo, **một** dạng cho mọi chỗ. Bản đầu dùng ` - ` làm dấu ngăn,
 * và nó vỡ ngay ở khoảng ngày: `05 - 09 - 2026 - 30 - 09 - 2026` là sáu con số
 * với năm dấu gạch giống hệt nhau, không mắt nào tách được đâu là ngày đâu là
 * ranh giới khoảng. Một dạng duy nhất thì không có chỗ nào để lệch.
 *
 * Nhận `Date`, hoặc chuỗi `YYYY-MM-DD`, hoặc chuỗi ISO đầy đủ. Giá trị rỗng
 * hay không đọc được trả `'—'`: một chỗ trống trên màn hình còn đọc được, một
 * chữ `Invalid Date` thì không.
 */
export function displayDate(value: DateLike): string {
  const date = toDate(value);
  if (!date) return '—';

  return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`;
}

/** `dd/MM/yyyy - dd/MM/yyyy`. Một đầu trống vẫn ra `—` ở đúng phía của nó. */
export function displayDateRange(from: DateLike, to: DateLike): string {
  return `${displayDate(from)} - ${displayDate(to)}`;
}

/**
 * Ngày và tháng, không năm: **`dd/MM`** — cùng quyết định.
 *
 * Dùng ở trục biểu đồ và những chỗ năm đã nói ở nhãn bao ngoài.
 */
export function displayDayMonth(value: DateLike): string {
  const date = toDate(value);
  if (!date) return '—';

  return `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}`;
}

/** Ngày + giờ phút: `dd/MM/yyyy HH:mm`, giờ theo **máy người xem**. */
export function displayDateTime(value: DateLike): string {
  const raw =
    value === null || value === undefined || value === ''
      ? null
      : value instanceof Date
        ? value
        : new Date(value);
  if (!raw || Number.isNaN(raw.getTime())) return '—';

  /*
    Phần ngày đọc theo **giờ địa phương**, khác `displayDate` ở trên.

    Một mốc có giờ là một khoảnh khắc thật, nên nó phải hiện theo đồng hồ của
    người đang xem: 23:30 ngày 20 ở UTC là 6:30 sáng ngày 21 ở UTC+7, và hiện
    "20/09" cạnh "06:30" là tự mâu thuẫn trên cùng một dòng.
  */
  const hh = pad(raw.getHours());
  const mm = pad(raw.getMinutes());
  return `${pad(raw.getDate())}/${pad(raw.getMonth() + 1)}/${raw.getFullYear()} ${hh}:${mm}`;
}

/**
 * Ngày tháng + giờ phút, không năm: `dd/MM HH:mm`, giờ theo **máy người xem**
 * — cùng lý lẽ với `displayDateTime`. Cho chỗ mốc luôn gần "bây giờ" (lượt
 * chạy job nền, APM-SYSMON-d), nơi năm chỉ chiếm chỗ.
 */
export function displayDayMonthTime(value: DateLike): string {
  const full = displayDateTime(value);
  if (full === '—') return full;
  // `dd/MM/yyyy HH:mm` → bỏ `/yyyy`.
  return `${full.slice(0, 5)}${full.slice(10)}`;
}

/**
 * Chuỗi `YYYY-MM-DD` đọc ở **UTC**, chuỗi ISO đầy đủ cũng vậy.
 *
 * Cả hai loại đều là ngày lịch khi đã tới đây, và đọc chúng ở giờ địa phương
 * là chỗ lệch một ngày ở mọi múi giờ âm — xem docblock đầu file.
 */
function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;

  const date =
    value instanceof Date
      ? value
      : typeof value === 'number'
        ? new Date(value)
        : /^\d{4}-\d{2}-\d{2}$/.test(value)
          ? parseDate(value)
          : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

const pad = (value: number): string => String(value).padStart(2, '0');
